import { isArray } from '@shared/is'

/*
 * @Description: 工具包
 */
export const NULL_SYMBOl = '-'

//formatWithNull
//格式化数据，如果数据为null或者undefined，则返回'-'
export const formatWithNull = (data: any) => (data === null || data === undefined ? NULL_SYMBOl : data)

// 格式化后台返回的时间
export function formatDateTime(time = '', span = ' ') {
	if (!time) {
		return NULL_SYMBOl
	}
	return time.replace('T', span)
}

// 禁选今天之后的日期
export function disabledDate(current: any) {
	return !!current && current > Date.now()
}
// 禁选今天之前的日期
export function disabledDateBefore(current: any) {
	return !!current && current < Date.now()
}

// 计算时间差的年份
export const calcFullYear = (time: number | Date | string) => {
	const date = new Date(time)
	const now = new Date()
	const year = now.getFullYear() - date.getFullYear()
	const month = now.getMonth() - date.getMonth()
	const day = now.getDate() - date.getDate()
	if (month < 0 || (month === 0 && day < 0)) {
		return year - 1
	}
	if (year < 0) {
		throw new Error('Calc age error')
	}
	return year
}

// 计算年或岁
export const calcAgeOrWorkAge = (date: string, unit: '年' | '岁' = '岁') => {
	if (!date) {
		return NULL_SYMBOl
	}
	return `${calcFullYear(date)} ${unit}`
}

// 添加单位
export const addUnit = (value: number | string, unit = '个') => {
	if (!value) {
		return NULL_SYMBOl
	}
	return `${value}${unit}`
}

// calculate the time difference
export const calcTimeDiff = (time: number | Date | string) => {
	const date = new Date(time)
	const now = new Date()
	const year = now.getFullYear() - date.getFullYear()
	const month = now.getMonth() - date.getMonth()
	const day = now.getDate() - date.getDate()
	const hour = now.getHours() - date.getHours()
	const minute = now.getMinutes() - date.getMinutes()
	const second = now.getSeconds() - date.getSeconds()
	if (year > 0) {
		return `${year}年前`
	}
	if (month > 0) {
		return `${month}月前`
	}
	if (day > 0) {
		return `${day}天前`
	}
	if (hour > 0) {
		return `${hour}小时前`
	}
	if (minute > 0) {
		return `${minute}分钟前`
	}
	if (second > 0) {
		return `${second}秒前`
	}
	return '刚刚'
}

/**
 * @description 处理无数据情况
 * @param {String} callValue 需要处理的值
 * @return string
 * */
export function formatValue(callValue: any) {
	// 如果当前值为数组,使用 / 拼接（根据需求自定义）
	if (isArray(callValue)) return callValue.length ? callValue.join(' / ') : '--'
	return callValue ?? '--'
}

/**
 * @description 根据枚举列表查询当需要的数据（如果指定了 label 和 value 的 key值，会自动识别格式化）
 * @param {String} callValue 当前单元格值
 * @param {Array} enumData 枚举列表
 * @param {String} type 过滤类型（目前只有 tag）
 * @return string
 * */
export function filterEnum(callValue: any, enumData: any, searchProps?: { [key: string]: any }, type?: string): string {
	const value = searchProps?.value ?? 'value'
	const label = searchProps?.label ?? 'label'
	const filterData = enumData.find((item: any) => item[value] === callValue)
	if (type == 'tag') return filterData?.tagType ? filterData.tagType : ''
	return filterData ? filterData[label] : '--'
}

/**
 * 复制文本
 * @param text 复制的内容
 * @param success 成功回调
 * @param fail 出错回调
 */
export function copyText(text: string, success?: () => void, fail?: (mess: string) => void) {
	text = text.replace(/(^\s*)|(\s*$)/g, '')
	if (!text) {
		typeof fail === 'function' && fail('复制的内容不能为空！')
		return
	}

	if (navigator.clipboard && window.isSecureContext) {
		// navigator clipboard 向剪贴板写文本
		navigator.clipboard.writeText(text)
		ElNotification.success('复制成功')
	} else {
		const id = 'the-clipboard'
		let clipboard = document.getElementById(id) as HTMLTextAreaElement
		if (!clipboard) {
			clipboard = document.createElement('textarea')
			clipboard.id = id
			;(clipboard as HTMLTextAreaElement).readOnly = true
			clipboard.style.cssText = 'font-size: 14px; position: fixed; top: -1000%; left: -1000%;'
			document.body.appendChild(clipboard)
		}
		clipboard.value = text
		clipboard.select()
		clipboard.setSelectionRange(0, text.length)
		const state = document.execCommand('copy')
		// clipboard.blur(); // 设置readOnly就不需要这行了
		if (state) {
			ElNotification.success('复制成功')
			typeof success === 'function' && success()
		} else {
			typeof fail === 'function' && fail('复制失败')
		}
	}
}

/**
 * @function enumToObjectArrayGenerator
 * @param {string} keyName
 * @param {string} valueName
 * @returns {enumToObjectArray}
 */
export function enumToObjectArrayGenerator<K extends string, V extends string>(keyName = 'key' as K, valueName = 'value' as V) {
	/**
	 * @function enumToObjectArray
	 * @param {Record<Key, Value>} enumParam
	 * @returns {Array<{ [k in K]: Key } & { [v in V]: Value }>}
	 */
	const enumToObjectArray = <Key extends string, Value>(enumParam: Record<Key, Value>) => {
		try {
			return Object.keys(enumParam).map(
				key =>
					({
						[keyName]: key,
						[valueName]: enumParam[key as Key],
					} as { [k in K]: Key } & { [v in V]: Value }),
			)
		} catch (error) {
			console.error('enumToObjectArrayGenerator: error: ', error)
			return []
		}
	}
	return enumToObjectArray
}

// const a = 1 as B
// a.a = '5'

// type B = number & { a: string }

// a.b = '1'
// Example usage:
// enum MyEnum {
// 	First = 1,
// 	Second = 2,
// 	Third = 3,
// }
// const enumToArray1 = enumToObjectArrayGenerator('key', 'value5')
// const myResult = enumToArray1(MyEnum)
// console.log(myResult[0].key, myResult[0].value) // First
// if (myResult[0].key === 'First') {
// 	console.log('ok')
// }
// if (myResult[0].key === 'aa') {
// 	console.log('ok')
// }
// if (myResult[0].value === 1) {
// 	console.log('ok')
// }
// if (myResult[0].value === '1') {
// 	console.log('ok')
// }
// if (myResult[0].b === '5a') {
// 	console.log('ok')
// }

// 枚举转换
export type EnumToArray<T = Record<string, any>> = (targetEnum: Recordable<unknown>) => T[]
export function enumFormatterGenerator(props = { value: 'value', key: 'key' }): EnumToArray {
	return targetEnum => {
		return Object.keys(targetEnum).map(key => ({ [props.value]: targetEnum[key], [props.key]: key }))
	}
}

// * 枚举转数组
export const enumToArray = enumFormatterGenerator()

/**
 * 验证密码强度
 * @param value
 */
export const checkPassModes = (value: string): number => {
	let modes = 0
	// 正则表达式验证符合要求的
	if (value.length < 1) return modes
	if (/\d/.test(value)) modes++ // 数字
	if (/[a-z]/.test(value)) modes++ // 小写
	if (/[A-Z]/.test(value)) modes++ // 大写
	if (/\W/.test(value)) modes++ // 特殊字符
	return modes
}

/**
 * 获取上传图片base64
 * @param file
 */
export const getFileBase64 = (file: Blob) =>
	new Promise((resolve, reject) => {
		const fileReader: any = new FileReader()
		fileReader.readAsDataURL(file)
		fileReader.onload = () => {
			const image = new Image()
			image.src = fileReader.result
			image.onload = () =>
				resolve({
					base64: fileReader.result,
					width: image.width,
					height: image.height,
				})
		}
		fileReader.onerror = (error: any) => reject(error)
	})

/**
 * 创建formData参数(只针对有图片上传)
 * @param {*} params
 */
export function createdFormData(params: any) {
	const formData = new FormData()
	Object.keys(params).forEach(key => {
		if (params[key] == 0 || (params[key] != '' && params[key] != undefined && params[key] != null)) {
			if (params[key] instanceof Array) {
				params[key].forEach((item: any) => {
					formData.append(`${key}[]`, item)
				})
			} else {
				formData.append(key, params[key])
			}
		}
	})
	return formData
}

// 判断某个dom元素是否包含在另一个dom元素中
export const contains = (parent: any, node: any) => {
	parent !== node && parent.contains(node)
}

// 格式化边界点位数据为二维数组
export const formatPolylinePoint = (polyline: any) => {
	const arr: any = []
	polyline?.split(';').map((item: any) => {
		const point = item.split(',').map(Number)
		const flag = point.includes(NaN)
		if (!flag) {
			arr.push(point)
		}
	})
	return arr
}

/**
 * @description 格式化表格单元格默认值
 * @param {Number} row 行
 * @param {Number} col 列
 * @param {String} callValue 当前单元格值
 * @return string
 * */
export function defaultFormat(row: number, col: number, callValue: any) {
	// 如果当前值为数组,使用 / 拼接（根据需求自定义）
	if (isArray(callValue)) return callValue.length ? callValue.join(' / ') : '--'
	return callValue ?? '--'
}
